import React, { useState, useRef, useCallback, useEffect} from 'react';
import { Form, Input, message, InputNumber, Button } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Image as AntdImage, Upload } from 'antd';
import styles from './AdminPanel.module.css';
import { Select } from 'antd';
import { uploadImagesForAddPackage, packageCheckUp, fetchTags} from './services/api';

const FORM_CONSTANTS = {
  MAX_PACKAGE_NAME_LENGTH: 150,
  MAX_IMAGES: 4,
  MAX_FILE_SIZE: 5 * 1024 * 1024,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png'],
};

const cleanQuery = (text) => {
  return text?.trim().replace(/\s+/g, ' ').toLowerCase() || '';
};

const AddPackage = () => {
  // Cost must be a number >= 0
  const abortControllerRef = useRef(null);
  const [form] = Form.useForm();
  const [isCheckingPackage, setIsCheckingPackage] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [tags, setTags] = useState([]);

  const daysCount = Form.useWatch('days', form) || 0;
  const totalNights = Form.useWatch('nights', form) || 0;
  const nightWiseStays = Form.useWatch('night_wise_stays', form) || [];
  const currentNightsSum = nightWiseStays.reduce((sum, stay) => sum + (stay?.nights || 0), 0);

  useEffect(() => {
    const controller = new AbortController();
    fetchTags(controller.signal)
      .then((data) => {
        setTags(data);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch tags:', error);
          message.error('Failed to load tags');
        }
      });
    return () => controller.abort();
}, []);


  const validatePackageName = useCallback(async (rule, value) => {
    const cleanedValue = cleanQuery(value);
    if (!cleanedValue) {
      throw new Error('Package name is required');
    }
    if (cleanedValue.length > FORM_CONSTANTS.MAX_PACKAGE_NAME_LENGTH) {
      throw new Error(`Package name must be ${FORM_CONSTANTS.MAX_PACKAGE_NAME_LENGTH} characters or less`);
    }

    setIsCheckingPackage(true);
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
     const { count } = await packageCheckUp(cleanedValue, abortControllerRef.current.signal, undefined);
      if (count > 0) {
        throw new Error('This package name is already used');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Package check error:', error.message);
      }
      throw error;
    } finally {
      setIsCheckingPackage(false);
    }
  }, []);

  const getBase64 = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(new Error(`FileReader error: ${error.message}`));
      }),
    []
  );

  const handlePreview = useCallback(async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj || file);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  }, [getBase64]);


  const handleChange = useCallback(({ fileList: newFileList }) => {
    const validFileList = newFileList.filter((file) => file.status !== 'removed').map
    ((file, index) => ({
      ...file,
      position: file.position || index + 1,
      sizeError: file.originFileObj && file.originFileObj.size > FORM_CONSTANTS.MAX_FILE_SIZE,
    }));
    if (validFileList.length > FORM_CONSTANTS.MAX_IMAGES) {
      message.error(`Maximum ${FORM_CONSTANTS.MAX_IMAGES} images allowed`);
      setFileList(validFileList.slice(0, FORM_CONSTANTS.MAX_IMAGES));
    } else {
       if (validFileList.some((file) => file.sizeError)) {
        message.error('Some images exceed 5MB limit and are highlighted in red. Please remove them.');
      }
      setFileList(validFileList);
    }
  }, []);

 const beforeUpload = useCallback((file) => {
  if (!FORM_CONSTANTS.ALLOWED_IMAGE_TYPES.includes(file.type)) {
    message.error('Only JPG, JPEG, or PNG files are allowed');
    return false;
  }
  if (file.size > FORM_CONSTANTS.MAX_FILE_SIZE) {
    message.error('File size must be under 5MB');
    return false;
  }
  return true;
}, []);

  const descriptionCheck = useCallback( (input) => {
   return input && input.trim() !== '' ? input.replace(/[^a-zA-Z\s]/g, "").replace(/\s+/g, ' ').trim().toLowerCase() : '';
  },[]);


  const retryUpload = async (files, cleanValues, signal, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await uploadImagesForAddPackage(files, cleanValues, signal);
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        if (attempt === maxRetries) throw new Error(`Image upload failed after ${maxRetries} attempts: ${error.message}`);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  };

 const onFinish = useCallback(
  async (values) => {
    try {
      if (!fileList || fileList.length !== FORM_CONSTANTS.MAX_IMAGES) {
        throw new Error(`${FORM_CONSTANTS.MAX_IMAGES} images are required`);
      }
      
      const files = fileList
        .map((file, index) => ({
          file: file.originFileObj,
          position: file.position || index + 1,
        }))
        .filter((item) => item.file instanceof File && FORM_CONSTANTS.ALLOWED_IMAGE_TYPES.includes(item.file.type) && item.file.size <= FORM_CONSTANTS.MAX_FILE_SIZE);
      if (files.length !== FORM_CONSTANTS.MAX_IMAGES) {
        throw new Error(`${FORM_CONSTANTS.MAX_IMAGES} valid image files are required`);
      }

      const submittedValues = {
        package_name: values.package_name?.trim()?.toLowerCase() || undefined,
        place_name: values.place_name?.trim()?.toLowerCase() || undefined,
        days: values.days && typeof values.days === 'number' && values.days >= 1 ? values.days : undefined,
        nights: typeof values.nights === 'number' && values.nights >= 1 ? values.nights : undefined,
        description: descriptionCheck(values.description) || undefined,
        overview: values.overview?.trim()?.toLowerCase() || undefined,
        highlights: values.highlights?.length > 0
          ? values.highlights
              .filter((h) => h && typeof h === 'string' && h.trim() !== '')
              .map((h) => h.trim().toLowerCase())
          : null,
        inclusions: values.inclusions?.length > 0
          ? values.inclusions
              .filter((i) => i && typeof i === 'string' && i.trim() !== '')
              .map((i) => i.trim().toLowerCase())
          : null,
        exclusions: values.exclusions?.length > 0
          ? values.exclusions
              .filter((e) => e && typeof e === 'string' && e.trim() !== '')
              .map((e) => e.trim().toLowerCase())
          : null,
        note: values.note?.length > 0
          ? values.note
              .filter((n) => n && typeof n === 'string' && n.trim() !== '')
              .map((n) => n.trim().toLowerCase())
          : null,
        day_wise_activities: values.day_wise_activities?.length > 0
          ? values.day_wise_activities
              .filter(
                (activity) =>
                  activity &&
                  typeof activity === 'object' &&
                  activity.day && typeof activity.day === 'number' && activity.day >= 1 &&
                  activity.heading && typeof activity.heading === 'string' && activity.heading.trim() !== '' &&
                  activity.activity && typeof activity.activity === 'string' && activity.activity.trim() !== ''
              )
              .map((activity) => ({
                day: activity.day,
                heading: activity.heading.trim().toLowerCase(),
                activity: activity.activity.trim().toLowerCase(),
              }))
          : null,
        night_wise_stays: values.night_wise_stays?.length > 0
          ? values.night_wise_stays
              .filter(
                (stay) =>
                  stay &&
                  typeof stay === 'object' &&
                  stay.nights && typeof stay.nights === 'number' && stay.nights >= 0 &&
                  stay.place && typeof stay.place === 'string' && stay.place.trim() !== ''
              )
              .map((stay) => ({
                nights: stay.nights,
                place: stay.place.trim().toLowerCase(),
              }))
          : null,
        tags: values.tags?.length > 0
          ? values.tags
              .filter((t) => t && typeof t === 'string' && t.trim() !== '')
              .map((t) => t.trim().toLowerCase())
          : null,
      };

      const cleanValues = Object.fromEntries(
        Object.entries(submittedValues).filter(([_, value]) => value !== undefined)
      );

      setIsUploading(true);
      abortControllerRef.current = new AbortController();
      const response = await retryUpload(files, cleanValues, abortControllerRef.current.signal);
      
      if (!response || response.status !== 201) {
        throw new Error('Failed to create package');
      }

      message.success('Package submitted successfully!');
      form.resetFields();
      form.setFieldsValue({ images: [] });
      setFileList([]);
    } catch (error) {
      if (error.name !== 'AbortError') {
        message.error(`Failed to submit package: ${error.message}`);
        console.error('Submit error:', error);
      }
    } finally {
      setIsUploading(false);
    }
  },
  [fileList, form, descriptionCheck]
);


  return (
    <div className={styles.addPackageWrapper}>

      <div className={styles.addPackageContainer}>
        <h2 className={styles.formTitle}>Add New Package</h2>
        <div style={{ fontWeight: 'normal', fontSize: '12px', textTransform: 'lowercase', width:'100%', justifyContent:'center', display:'flex',color:'red', position:'relative'}}>
          <ul>
            <li style={{marginBottom:'0.5rem'}}>use lower-case letters</li>
            <li style={{marginBottom:'0.5rem'}}>fill all the fields having * symbol</li>
            <li style={{marginBottom:'0.5rem'}}>Only images with jpg, jpeg, png formats are allowed</li>
            <li style={{marginBottom:'0.5rem'}}>Image size should be less than 5MB</li>
          </ul>
        </div>
        <Form
          form={form}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          layout="horizontal"
          onFinish={onFinish}
          onFinishFailed={() => message.error('Please fill all fields in the form.')}
          requiredMark={false}
        >
          <Form.Item
            label={<span><span className={styles.required}>*</span> Package Name</span>}
            name="package_name"
            hasFeedback
            validateTrigger={['onChange']}
            validateDebounce={1000}
            rules={[{ validator: validatePackageName }]}
            validateStatus={isCheckingPackage ? 'validating' : undefined}
          >
            <Input
              placeholder="Enter package name"
              aria-label="Package name input"
            />
          </Form.Item>

          <Form.Item
            label={<span><span className={styles.required}>*</span> Place Name</span>}
            name="place_name"
            hasFeedback
            rules={[{ required: true, message: 'Place name is required' }]}
          >
            <Input
              placeholder="Enter place name"
              aria-label="Place name input"
            />
          </Form.Item>

          <Form.Item
            label={<span><span className={styles.required}>*</span> Days</span>}
            name="days"
            hasFeedback
            rules={[{ required: true, message: 'Days is required' }, { type: 'number', min: 1, message: 'Days must be at least 1' }]}
          >
            <InputNumber
              placeholder="Days"
              aria-label="Days input"
              min={1}
              onChange={() => {
                const currentActivities = form.getFieldValue('day_wise_activities') || [];
                if (currentActivities.length > daysCount) {
                  form.setFieldsValue({
                    day_wise_activities: currentActivities.slice(0, daysCount),
                  });
                }
              }}
            />
          </Form.Item>

          <Form.Item
            label={<span><span className={styles.required}>*</span> Nights</span>}
            name="nights"
            hasFeedback
            rules={[
              { required: true, message: 'Nights is required' },
              { type: 'number', min: 1, message: 'Nights must be at least 1' },
              {
                validator: (_, value) => {
                  if (value && currentNightsSum > value) {
                    return Promise.reject(new Error(`Total nights in stays (${currentNightsSum}) exceeds package nights (${value})`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder="Nights"
              aria-label="Nights input"
              min={1}
              onChange={() => {
                const stays = form.getFieldValue('night_wise_stays') || [];
                let sum = 0;
                const validStays = stays.filter((stay) => {
                  if (!stay || !stay.nights) return false;
                  if (sum + stay.nights <= totalNights) {
                    sum += stay.nights;
                    return true;
                  }
                  return false;
                });
                if (validStays.length < stays.length) {
                  form.setFieldsValue({ night_wise_stays: validStays });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label="Overview"
            name="overview"
            wrapperCol={{ span: 20 }}
          >
            <Input.TextArea
              rows={6}
              placeholder="Provide a brief overview of the package"
            />
          </Form.Item>

          <Form.List name="highlights">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    key={key}
                    label={index === 0 ? 'Highlights' : ''}
                    wrapperCol={{ offset: index === 0 ? 0 : 4, span: 20 }}
                    required={false}
                  >
                    <div className={styles.dynamicField}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, whitespace: true, message: 'Highlight cannot be empty' }]}
                        noStyle
                      >
                        <Input
                          placeholder="Enter a highlight"
                          style={{ width: 'calc(100% - 32px)' }}
                        />
                      </Form.Item>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        className={styles.deleteIcon}
                      />
                    </div>
                  </Form.Item>
                ))}
                <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className={styles.addButton}
                  >
                    Add Highlight
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            label="Description"
            name="description"
            wrapperCol={{ span: 20 }}
          >
            <Input.TextArea
              rows={5}
              placeholder="Provide a description for search (e.g., beach holiday adventure)"
              aria-label="Description for search"
            />
          </Form.Item>

          <Form.List name="inclusions">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    key={key}
                    label={index === 0 ? 'Inclusions' : ''}
                    wrapperCol={{ offset: index === 0 ? 0 : 4, span: 20 }}
                    required={false}
                  >
                    <div className={styles.dynamicField}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, whitespace: true, message: 'Inclusion cannot be empty' }]}
                        noStyle
                      >
                        <Input
                          placeholder="Enter an inclusion"
                          style={{ width: 'calc(100% - 32px)' }}
                        />
                      </Form.Item>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        className={styles.deleteIcon}
                      />
                    </div>
                  </Form.Item>
                ))}
                <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className={styles.addButton}
                  >
                    Add Inclusion
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.List name="exclusions">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    key={key}
                    label={index === 0 ? 'Exclusions' : ''}
                    wrapperCol={{ offset: index === 0 ? 0 : 4, span: 20 }}
                    required={false}
                  >
                    <div className={styles.dynamicField}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, whitespace: true, message: 'Exclusion cannot be empty' }]}
                        noStyle
                      >
                        <Input
                          placeholder="Enter an exclusion"
                          style={{ width: 'calc(100% - 32px)' }}
                        />
                      </Form.Item>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        className={styles.deleteIcon}
                      />
                    </div>
                  </Form.Item>
                ))}
                <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className={styles.addButton}
                  >
                    Add Exclusion
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            label="Tags"
            name="tags"
            wrapperCol={{ span: 20 }}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Select tags for the package"
              style={{ width: '100%' }}
              options={tags.reduce((acc, { tag, category }) => {
                let group = acc.find((g) => g.label === category);
                if (!group) {
                  group = { label: category, options: [] };
                  acc.push(group);
                }
                group.options.push({ label: tag, value: tag });
                return acc;
              }, [])}
            />
          </Form.Item>

          <Form.List name="note">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    key={key}
                    label={index === 0 ? 'Notes' : ''}
                    wrapperCol={{ offset: index === 0 ? 0 : 4, span: 20 }}
                    required={false}
                  >
                    <div className={styles.dynamicField}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[{ required: true, whitespace: true, message: 'Note cannot be empty' }]}
                        noStyle
                      >
                        <Input
                          placeholder="Enter a note"
                          style={{ width: 'calc(100% - 32px)' }}
                        />
                      </Form.Item>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        className={styles.deleteIcon}
                      />
                    </div>
                  </Form.Item>
                ))}
                <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className={styles.addButton}
                  >
                    Add Note
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            label={<span><span className={styles.required}>*</span> Day-wise Activities</span>}
            wrapperCol={{ span: 20 }}
            
            className={styles.activitySection}
          >
            <Form.List name="day_wise_activities" rules={[{ required: true, message: 'Day-wise activities are required' }]}>
              {(fields, { add, remove }, { errors }) => (
                <div className={styles.activityList}>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className={styles.activityItem}>
                      <div className={styles.activityFields}>
                        <Form.Item
                          {...restField}
                          name={[name, 'day']}
                          rules={[{ required: true, message: 'Days are required' }]}
                          initialValue={index + 1}
                        >
                          <InputNumber
                            placeholder="Day"
                            min={1}
                            aria-label={`Day ${index + 1} input`}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'heading']}
                          style={{ width: '50%' }}
                          rules={[{ required: true, whitespace: true, message: 'Heading is required' }]}
                        >
                          <Input
                            placeholder="Enter activity"
                            aria-label={`Heading for Day ${index + 1}`}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'activity']}
                          style={{ width: '100%' }}
                          rules={[{ required: true, whitespace: true, message: 'Activity details are required' }]}
                        >
                          <Input.TextArea
                            placeholder="Enter activity details"
                            rows={6}
                            aria-label={`Activity details for Day ${index + 1}`}
                          />
                        </Form.Item>
                        <button
                          onClick={() => remove(name)}
                          className={styles.deleteButton}
                          aria-label="Remove activity"
                        >
                          <MinusCircleOutlined />
                        </button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ day: fields.length + 1 })}
                    block
                    icon={<PlusOutlined />}
                    className={styles.addButton}
                    disabled={fields.length >= daysCount}
                  >
                    Add Day-wise Activity
                  </Button>
                  <Form.ErrorList errors={errors} />
                </div>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            label="Night-wise Stays"
            wrapperCol={{ span: 20 }}
            className={styles.activitySection}
          >
            <Form.List name="night_wise_stays">
              {(fields, { add, remove }, { errors }) => (
                <div className={styles.activityList}>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className={styles.activityItem}>
                      <div className={styles.activityFields}>
                        <Form.Item
                          {...restField}
                          name={[name, 'nights']}
                          rules={[
                            { required: true, message: 'Nights is required' },
                            { type: 'number', min: 1, message: 'Nights must be at least 1' },
                            {
                              validator: (_, value) => {
                                const stays = form.getFieldValue('night_wise_stays') || [];
                                const sum = stays.reduce((acc, stay, idx) => 
                                  acc + (idx === name ? (value || 0) : (stay?.nights || 0)), 0);
                                if (sum > totalNights) {
                                  return Promise.reject(new Error(`Total nights (${sum}) exceeds package nights (${totalNights})`));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <InputNumber
                            placeholder="Nights"
                            min={1}
                            aria-label={`Nights for stay ${index + 1}`}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'place']}
                          style={{ width: '50%' }}
                          rules={[{ required: true, whitespace: true, message: 'Place is required' }]}
                        >
                          <Input
                            placeholder="Enter place name"
                            aria-label={`Place for stay ${index + 1}`}
                          />
                        </Form.Item>
                        <button
                          onClick={() => remove(name)}
                          className={styles.deleteButton}
                          aria-label="Remove stay"
                        >
                          <MinusCircleOutlined />
                        </button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className={styles.addButton}
                    disabled={currentNightsSum >= totalNights}
                  >
                    Add Night-wise Stay
                  </Button>
                  <Form.ErrorList errors={errors} />
                </div>
              )}
            </Form.List>
          </Form.Item>

          
         <Form.Item
            label={<span><span className={styles.required}>*</span> Images</span>}
            name="images"
            wrapperCol={{ offset: 4, span: 20 }}
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.length !== FORM_CONSTANTS.MAX_IMAGES) {
                    return Promise.reject(new Error(`Exactly ${FORM_CONSTANTS.MAX_IMAGES} images are required`));
                  }
                  if (value.some((file) => file.sizeError)) {
                    return Promise.reject(new Error('Some images exceed 5MB limit. Please remove highlighted images.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            getValueFromEvent={({ fileList }) => fileList}
          >
            <Upload
              listType="picture-card"
              fileList={fileList}
              onPreview={handlePreview}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              className={styles.uploadButton}
              customRequest={({ onSuccess }) => onSuccess('ok')}
              accept={FORM_CONSTANTS.ALLOWED_IMAGE_TYPES.join(',')}
              itemRender={(originNode, file) => (
                  <div style={{ border: file.sizeError ? '2px solid red' : 'none', padding: file.sizeError ? '2px' : '0' }}>
                    {originNode}
                  </div>
                )}
            >
              {fileList.length >= FORM_CONSTANTS.MAX_IMAGES ? null : (
                <button style={{ border: 0, background: 'none' }} type="button">
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </button>
              )}
            </Upload>
          </Form.Item>
          {previewImage && (
            <AntdImage
              wrapperStyle={{ display: 'none' }}
              preview={{
                visible: previewOpen,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(''),
              }}
              src={previewImage}
            />
          )}

          <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
            <Button
              type="primary"
              htmlType="submit"
              disabled={isCheckingPackage || isUploading}
              className={styles.submitButton}
              aria-label="Submit package form"
            >
              {(isCheckingPackage || isUploading) ? 'Submitting...' : 'Submit'}
            </Button>
          </Form.Item>

         
        </Form>
      </div>
    </div>
  );
};

export default AddPackage;